/**
 * TenantLogin
 *
 * Branded login page for sandbox tenants.
 * Route: /:tenantSlug/login
 *
 * - Fetches tenant branding via TenantBrandingContext
 * - Renders tenant logo + colors instead of REDEX branding
 * - On success, stores JWT and redirects to /:tenantSlug/portal
 */

import React, { useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { TenantBrandingProvider, useTenantBranding } from '../contexts/TenantBrandingContext';
import { useUser } from '../contexts/UserContext';
import { toast } from 'react-hot-toast';

// ─── Inner form — reads branding from context ─────────────────────────────────
function TenantLoginForm() {
  const { branding, isLoading, notFound } = useTenantBranding();
  const { tenantSlug: slug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading portal…</p>
        </div>
      </div>
    );
  }

  if (notFound || !branding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-sm px-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Portal not found</h1>
          <p className="text-gray-500 mb-6">
            The portal <strong>{slug}</strong> doesn't exist or hasn't launched yet.
          </p>
          <a href="/login" className="text-sm text-blue-600 hover:underline">
            Return to main login
          </a>
        </div>
      </div>
    );
  }

  const primary = branding.primaryColor;
  const accent  = branding.accentColor;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${apiBase}/portal/${encodeURIComponent(branding!.slug)}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.message || 'Invalid credentials');
        return;
      }
      // Store token — same key as main app so useUser picks it up on next load.
      // Force a full navigation (not React Router nav) so UserContext re-initializes
      // from the new token and correctly hydrates sandboxTenantId.
      localStorage.setItem('authToken', json.token);
      window.location.href = `/${branding!.slug}/portal`;
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: `${primary}08`, fontFamily: branding.fontFamily || 'Inter, sans-serif' }}
    >
      {/* Branded header bar */}
      <div style={{ backgroundColor: primary }} className="py-4 px-6">
        <div className="max-w-md mx-auto flex items-center gap-3">
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.companyName}
              className="h-10 w-auto object-contain"
            />
          ) : (
            <span className="text-white text-xl font-semibold tracking-tight">
              {branding.companyName}
            </span>
          )}
        </div>
      </div>

      {/* Login card */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            {/* Heading */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900">Sign in to your portal</h1>
              <p className="text-sm text-gray-500 mt-1">
                {branding.companyName} investor portal
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email address
                </label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': accent } as React.CSSProperties}
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 rounded-lg text-white text-sm font-semibold transition-opacity disabled:opacity-60"
                style={{ backgroundColor: primary }}
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            {/* Footer */}
            <p className="mt-6 text-xs text-center text-gray-400">
              Don't have access?{' '}
              <span className="text-gray-500">
                Contact your portal administrator.
              </span>
            </p>
          </div>

          {/* Trial expiry notice */}
          {branding.trialExpiresAt && (() => {
            const daysLeft = Math.ceil(
              (new Date(branding.trialExpiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
            );
            if (daysLeft > 0 && daysLeft <= 3) {
              return (
                <p className="mt-4 text-xs text-center text-amber-600">
                  Trial expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}.
                </p>
              );
            }
            return null;
          })()}
        </div>
      </div>

    </div>
  );
}

// ─── Outer wrapper with provider ─────────────────────────────────────────────
export default function TenantLogin() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { isAuthenticated, sandboxTenantId } = useUser();

  // Already logged into this tenant → go straight to their portal
  if (isAuthenticated && sandboxTenantId === tenantSlug) {
    return <Navigate to={`/${tenantSlug}/portal`} replace />;
  }

  if (!tenantSlug) return <Navigate to="/login" replace />;

  return (
    <TenantBrandingProvider slug={tenantSlug}>
      <TenantLoginForm />
    </TenantBrandingProvider>
  );
}
